// src/services/serviceFactory.ts - Updated to support session injection
import { createFirestoreService } from '@/platform/firebase/firestore'
import type { IFirestoreService } from '@/platform/firebase/firestore'
import { PersonService } from '@/services/person/PersonService'
import { OrganizationService } from '@/services/organization/OrganizationService'
import { TenantService } from '@/services/tenant/TenantService'
import { SaasAdminService } from '@/services/saasAdmin/SaasAdminService'
import { TransactionService } from '@/services/transaction/TransactionService'
import { TenantSearch } from '@/services/tenant/TenantSearch'
import { AgentService } from '@/services/agent/AgentService'
import { AgentSearch } from '@/services/agent/AgentSearch'
import { TaskService } from '@/services/task/TaskService'
import { FileService } from '@/services/file/FileService'
import { useSession } from '@/stores/authStore'

type Session = { tenantId?: string; yardId?: string; userId?: string }

let _firestore: IFirestoreService | null = null
let _firestorePromise: Promise<IFirestoreService> | null = null

async function getFirestore(): Promise<IFirestoreService> {
  if (_firestore) return _firestore
  if (_firestorePromise) return _firestorePromise

  _firestorePromise = createFirestoreService()
  _firestore = await _firestorePromise
  return _firestore
}

async function getSession(): Promise<Session> {
  const session = useSession()

  // Wait for session to be ready
  let attempts = 0
  while (!session.tenantId && attempts < 50) {
    // Wait up to 5 seconds
    await new Promise((resolve) => setTimeout(resolve, 100))
    attempts++
  }

  return {
    tenantId: session.tenantId,
    yardId: session.yardId,
    userId: session.userId,
  }
}

export async function getPersonService(): Promise<PersonService> {
  const [firestore, session] = await Promise.all([getFirestore(), getSession()])
  return new PersonService(firestore, session)
}

export async function getOrganizationService(): Promise<OrganizationService> {
  const [firestore, session] = await Promise.all([getFirestore(), getSession()])
  return new OrganizationService(firestore, session)
}

export async function getTenantService(): Promise<TenantService> {
  const [firestore, session] = await Promise.all([getFirestore(), getSession()])
  return new TenantService(firestore, session)
}

export async function getSaasAdminService(): Promise<SaasAdminService> {
  const [firestore, session] = await Promise.all([getFirestore(), getSession()])
  return new SaasAdminService(firestore, session)
}

export async function getTransactionService(): Promise<TransactionService> {
  const [firestore, session] = await Promise.all([getFirestore(), getSession()])
  return new TransactionService(firestore, session)
}

export async function getAgentService(): Promise<AgentService> {
  const [firestore, session] = await Promise.all([getFirestore(), getSession()])
  return new AgentService(firestore, session)
}

export async function getTaskService(): Promise<TaskService> {
  const [firestore, session] = await Promise.all([getFirestore(), getSession()])
  return new TaskService(firestore, session)
}

export async function getFileService(): Promise<FileService> {
  const [firestore, session] = await Promise.all([getFirestore(), getSession()])
  return new FileService(firestore, session)
}

// Search helpers
export async function getTenantSearch(session: { tenantId?: string; userId?: string }) {
  const firestore = await getFirestore()
  return new TenantSearch(session, firestore)
}

export async function getAgentSearch() {
  const [firestore, session] = await Promise.all([getFirestore(), getSession()])
  return new AgentSearch(session, firestore)
}
